import { useState, useRef, useEffect, useCallback } from "react";
import type { QueryClient } from "@tanstack/react-query";
import { useModalOverlayClick } from "@/common/hooks/useModalOverlayClick";
import { useObjectSelectionListener } from "@/app/providers/ObjectSelectionContext";
import { resolveImageUrl } from "../resolveImageUrl";
import { normalizePlaceName } from "../lib/placeFields";
import { transformPlaceCard } from "../lib/placeMapping";
import { enrichPlace, publishPlace, unpublishPlace } from "../lib/placesApi";
import type { PlaceObject } from "../types";

export interface UsePlaceEnrichmentDeps {
  /** Full unfiltered place list (used by the open-by-name + selection listeners). */
  allPlaces: PlaceObject[];
  /** Filtered/sorted list currently shown (used to preload hero images). */
  places: PlaceObject[];
  queryClient: QueryClient;
  placeQueryKey: readonly unknown[];
}

/**
 * The place detail-overlay enrichment state machine.
 *
 * Owns the overlay state cluster, the locally-saved-notes ref, the gear-menu
 * ref, the overlay-click hook, the enrichment handlers, the lazy hero-image
 * preloader, and the two cross-module open listeners (the synchronous
 * `xavior:open-object-by-name` event and the ObjectSelectionContext listener).
 * The listeners call back into handlePlaceClick via a ref so the latest closure
 * runs.
 */
export function usePlaceEnrichment(deps: UsePlaceEnrichmentDeps) {
  const { allPlaces, places, queryClient, placeQueryKey } = deps;

  const [selectedPlace, setSelectedPlace] = useState<PlaceObject | null>(null);
  const [enrichedCard, setEnrichedCard] = useState<any>(null);
  const [isEnriching, setIsEnriching] = useState(false);
  const [isChangingPlace, setIsChangingPlace] = useState(false);
  const [showOverlayGearMenu, setShowOverlayGearMenu] = useState(false);
  const overlayGearRef = useRef<HTMLDivElement>(null);
  const [isPublishing, setIsPublishing] = useState(false);
  const [publishedUrl, setPublishedUrl] = useState<string | null>(null);
  // Local note overrides — persists across card open/close until page refresh
  const savedNotesRef = useRef<Record<string, string>>({});

  // Listen for "open [place name]" from chat — synchronous event so ChatBox knows if we handled it
  useEffect(() => {
    const normalize = (s: string) =>
      normalizePlaceName(s, { stripEnglishArticle: true });
    const handleOpenByName = (
      event: CustomEvent<{
        name: string;
        handled: boolean;
        displayName: string;
      }>,
    ) => {
      if (event.detail.handled) return;
      const needle = normalize(event.detail.name);
      const place =
        allPlaces.find((p) => normalize(p.name || p.title || "") === needle) ||
        allPlaces.find((p) =>
          normalize(p.name || p.title || "").includes(needle),
        );
      if (place && handlePlaceClickRef.current) {
        event.detail.handled = true;
        event.detail.displayName =
          place.name || place.title || event.detail.name;
        handlePlaceClickRef.current(place);
      }
    };
    window.addEventListener(
      "xavior:open-object-by-name",
      handleOpenByName as EventListener,
    );
    return () =>
      window.removeEventListener(
        "xavior:open-object-by-name",
        handleOpenByName as EventListener,
      );
  }, [allPlaces]);

  // Listen for cross-module object selection (e.g., "open chez georges" from chat)
  const handlePlaceClickRef = useRef<((place: PlaceObject) => void) | null>(
    null,
  );
  useObjectSelectionListener(
    "place",
    useCallback(
      (placeIdOrName: string) => {
        const normalize = (s: string) =>
          normalizePlaceName(s, { stripEnglishArticle: false });
        console.log(
          "[PlaceListModule] Object selection received:",
          placeIdOrName,
          "allPlaces:",
          allPlaces.map((p) => p.name || p.title),
        );
        const needle = normalize(placeIdOrName);
        const place =
          allPlaces.find((p) => p.id === placeIdOrName) ||
          allPlaces.find(
            (p) => normalize(p.name || p.title || "") === needle,
          ) ||
          allPlaces.find((p) =>
            normalize(p.name || p.title || "").includes(needle),
          );
        if (place && handlePlaceClickRef.current) {
          console.log(
            "[PlaceListModule] Opening place:",
            place.name || place.title,
          );
          handlePlaceClickRef.current(place);
        } else {
          console.warn("[PlaceListModule] Place not found for:", placeIdOrName);
        }
      },
      [allPlaces],
    ),
  );

  // Preload place images lazily (stagger to avoid flooding the proxy)
  useEffect(() => {
    let cancelled = false;
    const urls = places
      .slice(0, 20)
      .map((p) => resolveImageUrl(p.imageUrl || p.images?.[0]?.url))
      .filter(Boolean);
    urls.forEach((url, i) => {
      setTimeout(() => {
        if (cancelled) return;
        const img = new Image();
        img.src = url!;
      }, i * 50);
    });
    return () => {
      cancelled = true;
    };
  }, [places]);

  const handleCloseOverlay = () => {
    setSelectedPlace(null);
    setEnrichedCard(null);
    setIsChangingPlace(false);
    setShowOverlayGearMenu(false);
    setPublishedUrl(null);
  };

  const handlePublishPlace = async () => {
    if (!selectedPlace) return;
    setShowOverlayGearMenu(false);
    setIsPublishing(true);
    try {
      const data = await publishPlace(selectedPlace.id);
      setPublishedUrl(data.url);
      navigator.clipboard.writeText(data.url).catch(() => {});
    } catch (error) {
      console.error("[PlaceListModule] Publish failed:", error);
    } finally {
      setIsPublishing(false);
    }
  };

  const handleUnpublishPlace = async () => {
    if (!selectedPlace) return;
    setShowOverlayGearMenu(false);
    try {
      await unpublishPlace(selectedPlace.id);
      setPublishedUrl(null);
    } catch (error) {
      console.error("[PlaceListModule] Unpublish failed:", error);
    }
  };

  const [placeDetailRef, handlePlaceOverlayClick, handlePlaceOverlayMouseDown] =
    useModalOverlayClick(handleCloseOverlay, 30);

  const handlePlaceClick = async (place: PlaceObject) => {
    setSelectedPlace(place);

    // Enough cached data to show instantly (don't require rating or image)
    const hasFullData =
      place.googlePlaceId || (place.description && place.address);

    if (hasFullData) {
      const cachedCard = {
        placeId: place.googlePlaceId,
        title: place.title || place.name,
        subtitle: place.address,
        address: place.address,
        rating: place.rating,
        reviewCount: place.reviewCount,
        status: place.status,
        priceLevel: place.priceLevel,
        googleMapsUri: place.googleMapsUri,
        websiteUri: place.websiteUri,
        phoneNumber: place.phoneNumber,
        description: place.description,
        images:
          place.images || (place.imageUrl ? [{ url: place.imageUrl }] : []),
        sections: place.sections || [],
      };

      const transformedCached = transformPlaceCard(place, cachedCard);
      // Apply locally saved notes not yet picked up by cache
      if (savedNotesRef.current[place.id]) {
        transformedCached.userNotes = savedNotesRef.current[place.id];
      }
      setEnrichedCard(transformedCached);
      setIsEnriching(false);

      // Only fetch fresh data if place is missing key info (description, images)
      // Skip if already fully enriched to avoid disconcerting refresh
      const needsEnrichment = !place.description || !place.images?.length;
      if (needsEnrichment) {
        fetchEnrichmentInBackground(place, true);
      }
    } else {
      setIsEnriching(true);
      await fetchEnrichmentInBackground(place, false);
    }
  };

  // Keep ref in sync for cross-module selection listener
  handlePlaceClickRef.current = handlePlaceClick;

  // Fetch enrichment data (used both for initial load and background refresh)
  // silent = true: only update cache, don't update UI (for background refresh)
  // silent = false: update both cache and UI (for initial load)
  const fetchEnrichmentInBackground = async (
    place: PlaceObject,
    silent: boolean = false,
  ) => {
    try {
      const enrichResponse = await enrichPlace(place.id);

      const card = enrichResponse.card;
      const transformedCard = transformPlaceCard(place, card);

      // Apply enriched fields into cache in-place to preserve list order.
      // Avoid invalidation/refetch here because backend sort order can move the
      // clicked place to the top, which feels like list "jumping".
      const enrichedPatch: Partial<PlaceObject> = {
        title: card?.title || place.title,
        name: card?.title || place.name,
        address: card?.address || place.address,
        googlePlaceId:
          card?.placeId || card?.googlePlaceId || place.googlePlaceId,
        rating: card?.rating ?? place.rating,
        reviewCount: card?.reviewCount ?? place.reviewCount,
        status: card?.status ?? place.status,
        priceLevel: card?.priceLevel ?? place.priceLevel,
        phoneNumber: card?.phoneNumber ?? place.phoneNumber,
        websiteUri: card?.websiteUri ?? place.websiteUri,
        googleMapsUri: card?.googleMapsUri ?? place.googleMapsUri,
        primaryType: card?.primaryType ?? place.primaryType,
        // Prefer the longer description (AI-generated from DB) over Google's short one
        description:
          place.description &&
          place.description.length > (card?.description?.length || 0)
            ? place.description
            : (card?.description ?? place.description),
        imageUrl: resolveImageUrl(card?.images?.[0]?.url) || place.imageUrl,
        images:
          card?.images?.map((img: any) => ({
            url: resolveImageUrl(
              img.fullsizeUrl || img.thumbnailUrl || img.url,
            ),
            attribution: img.caption,
            isMap: Boolean(img.isMap),
          })) || place.images,
        sections: Array.isArray(card?.sections)
          ? card.sections.map((section: any) => ({
              title: section.title,
              ...(section.body ? { body: section.body } : {}),
              items: Array.isArray(section.items)
                ? section.items
                    .map((item: any) =>
                      typeof item === "string" ? item : item?.value,
                    )
                    .filter(Boolean)
                : [],
            }))
          : place.sections,
      };

      queryClient.setQueryData(placeQueryKey, (old: any) => {
        if (!old || !Array.isArray(old.items)) return old;
        const updatedItems = old.items.map((item: any) =>
          item?.id === place.id ? { ...item, ...enrichedPatch } : item,
        );
        return { ...old, items: updatedItems };
      });

      // Always update enriched card with latest data, but preserve locally saved notes
      setEnrichedCard((prev: any) => ({
        ...transformedCard,
        userNotes:
          savedNotesRef.current[place.id] ||
          prev?.userNotes ||
          transformedCard.userNotes,
      }));
      if (!silent) {
        setIsEnriching(false);
      }
    } catch (error) {
      console.error("[PlaceListModule] Failed to enrich place:", error);

      // Only handle error UI if not silent
      if (!silent) {
        // Set a fallback card if enrichment fails and we have no data
        const photoUrl = resolveImageUrl(
          place.imageUrl || place.images?.[0]?.url,
        );
        const hasCachedData =
          place.googlePlaceId || place.rating || place.reviewCount;

        if (!hasCachedData) {
          setEnrichedCard({
            objectId: place.id,
            etag: place._etag,
            title: place.title || place.name || "Untitled Place",
            subtitle: place.address,
            images: photoUrl
              ? [
                  {
                    fullsizeUrl: photoUrl,
                    thumbnailUrl: photoUrl,
                  },
                ]
              : undefined,
            sections: [
              place.address
                ? {
                    title: "Address",
                    items: [{ value: place.address }],
                  }
                : null,
              place.userNotes
                ? {
                    title: "Notes",
                    body: place.userNotes,
                  }
                : null,
            ].filter(Boolean),
          });
        }
        setIsEnriching(false);
      }
    }
  };

  return {
    // state
    selectedPlace,
    enrichedCard,
    setEnrichedCard,
    isEnriching,
    isChangingPlace,
    setIsChangingPlace,
    showOverlayGearMenu,
    setShowOverlayGearMenu,
    isPublishing,
    publishedUrl,
    // refs
    savedNotesRef,
    overlayGearRef,
    placeDetailRef,
    // handlers
    handlePlaceClick,
    handleCloseOverlay,
    handlePublishPlace,
    handleUnpublishPlace,
    handlePlaceOverlayClick,
    handlePlaceOverlayMouseDown,
  };
}
